import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import ImageUploader from "./ImageUploader";
import ListingPhotoEditor from "./ListingPhotoEditor";

const ImageGalleryManager = ({
  initialPhotos = [],
  listingId,
  entityName,
  collectionName,
  isCarousel,
  form,
  csrfToken,
}) => {
  const [photos, setPhotos] = useState([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState([]);

  // New state for editor
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);

  useEffect(() => {
    const photosWithClientIds = initialPhotos.map((photo) => ({
      ...photo,
      clientId: photo.id || `client-${Date.now()}-${Math.random()}`,
      // Ensure we have a URL, alt text, and caption
      imageUrlForFrontend:
        photo.image_url_for_frontend || photo.image?.url || photo.preview,
      altText: photo.image_alt_text || "",
      caption: photo.caption || "",
      hasRightsToUse: photo.has_rights_to_use || false,
      isNew: !photo.id, // Mark if it's a new photo (no ID yet)
      _destroy: photo._destroy || false,
    }));
    setPhotos(photosWithClientIds);
  }, [initialPhotos]);

  const handleFilesAdded = useCallback(
    async (newFiles) => {
      const newPhotoObjects = newFiles.map((file) => ({
        clientId: `client-${Date.now()}-${Math.random()}`,
        file: file,
        preview: URL.createObjectURL(file),
        altText: "",
        caption: "",
        hasRightsToUse: false,
        isNew: true,
        featured: false, // Default, can be changed
        _destroy: false,
        isLoading: true, // For UI feedback during upload
        error: null, // To store any upload error for this specific photo
      }));

      // Add to local state immediately for UI responsiveness
      setPhotos((prevPhotos) => [...prevPhotos, ...newPhotoObjects]);

      // Now, upload each new photo
      for (const newPhoto of newPhotoObjects) {
        const formData = new FormData();
        formData.append("photo[image]", newPhoto.file);
        formData.append("photo[photographic_type]", entityName);
        if (listingId) {
          formData.append("photo[photographic_id]", listingId);
        }
        if (collectionName === "hero_photos_attributes") {
          formData.append("photo[display_type]", "hero_type");
        } else if (collectionName === "carousel_photos_attributes") {
          formData.append("photo[display_type]", "carousel_type");
        }
        if (isCarousel) {
          formData.append("photo[featured]", newPhoto.featured || false);
        }
        formData.append("photo[image_alt_text]", newPhoto.altText || "");
        formData.append("photo[caption]", newPhoto.caption || "");
        formData.append(
          "photo[has_rights_to_use]",
          newPhoto.hasRightsToUse || false
        );

        console.log(
          "[ImageGalleryManager] Uploading new file:",
          newPhoto.file.name,
          " FormData entries:"
        );
        for (let [key, value] of formData.entries()) {
          console.log("  ", key, value);
        }

        try {
          const response = await fetch("/api/admin/photos", {
            method: "POST",
            headers: {
              Accept: "application/json",
              "X-CSRF-Token": csrfToken,
              // 'Content-Type' is not set for FormData; browser handles it with the correct boundary
            },
            body: formData,
          });

          if (!response.ok) {
            let errorData;
            try {
              errorData = await response.json();
            } catch (e) {
              errorData = { errors: [response.statusText] }; // Ensure errors is an array
            }
            const errorMessage = (errorData.errors || ["Upload failed"]).join(
              ", "
            );
            console.error(
              "[ImageGalleryManager] Error uploading new file:",
              newPhoto.file.name,
              response.status,
              errorData
            );
            setPhotos((prev) =>
              prev.map((p) =>
                p.clientId === newPhoto.clientId
                  ? { ...p, isLoading: false, error: errorMessage }
                  : p
              )
            );
            continue; // Move to the next file
          }

          const savedPhotoWrapper = await response.json();
          const savedPhotoData = savedPhotoWrapper.photo;

          if (!savedPhotoData || !savedPhotoData.id) {
            console.error(
              "[ImageGalleryManager] Invalid photo data received from server for new upload:",
              savedPhotoWrapper
            );
            setPhotos((prev) =>
              prev.map((p) =>
                p.clientId === newPhoto.clientId
                  ? {
                      ...p,
                      isLoading: false,
                      error: "Invalid server response.",
                    }
                  : p
              )
            );
            continue;
          }

          console.log(
            "[ImageGalleryManager] New file uploaded successfully:",
            savedPhotoData
          );

          // Update the specific photo in state with server data
          setPhotos((prevPhotos) =>
            prevPhotos.map((p) => {
              if (p.clientId === newPhoto.clientId) {
                return {
                  ...p,
                  id: savedPhotoData.id,
                  imageUrlForFrontend: savedPhotoData.image_url_for_frontend,
                  altText: savedPhotoData.image_alt_text || "",
                  caption: savedPhotoData.caption || "",
                  hasRightsToUse: savedPhotoData.has_rights_to_use || false,
                  featured:
                    savedPhotoData.featured !== undefined
                      ? savedPhotoData.featured
                      : p.featured,
                  isNew: false,
                  isLoading: false,
                  file: null, // Clear file object as it's now on server
                  error: null, // Clear any previous error
                };
              }
              return p;
            })
          );
        } catch (error) {
          console.error(
            "[ImageGalleryManager] Network error uploading file:",
            newPhoto.file.name,
            error
          );
          setPhotos((prev) =>
            prev.map((p) =>
              p.clientId === newPhoto.clientId
                ? {
                    ...p,
                    isLoading: false,
                    error: "Network error during upload.",
                  }
                : p
            )
          );
        }
      }
    },
    [entityName, listingId, collectionName, isCarousel, csrfToken, setPhotos]
  );

  const handleRemovePhoto = useCallback((clientIdToRemove) => {
    setPhotos((prevPhotos) =>
      prevPhotos
        .map((p) => {
          if (p.clientId === clientIdToRemove) {
            if (!p.isNew && p.id) {
              // If it's an existing photo with an ID
              setDeletedPhotoIds((prevDeleted) => [...prevDeleted, p.id]);
              return { ...p, _destroy: true };
            }
            // If it's a new photo (no ID) or already marked, filter it out
            return null;
          }
          return p;
        })
        .filter(Boolean)
    );
  }, []);

  const handleInputChange = useCallback((clientId, field, value) => {
    setPhotos((prevPhotos) =>
      prevPhotos.map((p) =>
        p.clientId === clientId ? { ...p, [field]: value } : p
      )
    );
  }, []);

  const handleGenerateAlt = useCallback(
    async (clientId) => {
      const photo = photos.find((p) => p.clientId === clientId);
      if (!photo) return;

      const imageUrl =
        photo.imageUrlForFrontend || (photo.image && photo.image.url) || null;
      if (!imageUrl) {
        setPhotos((prev) =>
          prev.map((p) =>
            p.clientId === clientId
              ? {
                  ...p,
                  error:
                    p.error ||
                    "Please wait for upload to finish before generating alt.",
                }
              : p
          )
        );
        return;
      }

      // Mark generating state
      setPhotos((prev) =>
        prev.map((p) =>
          p.clientId === clientId ? { ...p, isGeneratingAlt: true } : p
        )
      );

      try {
        const params = new URLSearchParams();
        params.append("model_name", entityName);
        if (listingId && !isNaN(listingId)) {
          params.append("model_id", String(listingId));
        }
        params.append("image_url", imageUrl);

        const resp = await fetch("/api/gpt/describe_image", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: params.toString(),
        });

        let data;
        try {
          data = await resp.json();
        } catch (e) {
          data = null;
        }

        if (!resp.ok || !data || (!data.description && !data.error)) {
          throw new Error(
            (data && (data.error || data.message)) ||
              `Alt generation failed (status ${resp.status}).`
          );
        }

        if (data.description) {
          setPhotos((prev) =>
            prev.map((p) =>
              p.clientId === clientId
                ? { ...p, altText: data.description, error: null }
                : p
            )
          );
        } else if (data.error) {
          console.error("[ImageGalleryManager] ERROR from API:", data.error);
          throw new Error(data.error);
        }
      } catch (e) {
        setPhotos((prev) =>
          prev.map((p) =>
            p.clientId === clientId
              ? { ...p, error: e.message || "Failed to generate alt text." }
              : p
          )
        );
      } finally {
        setPhotos((prev) =>
          prev.map((p) =>
            p.clientId === clientId ? { ...p, isGeneratingAlt: false } : p
          )
        );
      }
    },
    [photos, entityName, listingId, csrfToken]
  );

  // --- Editor Interaction Handlers ---
  const handleOpenEditor = (photo) => {
    console.log(
      "[ImageGalleryManager] handleOpenEditor triggered for photo:",
      photo
    );
    setEditingPhoto(photo);
    setIsEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setEditingPhoto(null);
  };

  const handleSaveEditedImage = async (photoIdentifier, editedDataUrl) => {
    const photoToUpdate = photos.find(
      (p) => p.clientId === photoIdentifier || p.id === photoIdentifier
    );

    if (!photoToUpdate) {
      console.error(
        "[ImageGalleryManager] Could not find photo to update:",
        photoIdentifier
      );
      handleCloseEditor();
      return;
    }

    console.log(
      "[ImageGalleryManager] Saving edited image. Photo to update:",
      photoToUpdate,
      "New data URL (prefix):",
      editedDataUrl.substring(0, 30) + "..."
    );

    // Determine if it's an update to an existing persisted photo or a new/unpersisted one
    // A photo is considered existing if it has an ID and isNew is explicitly false (or was never true).
    // If isNew is true, even if it has an ID (e.g. from a previous failed save attempt or optimistic update),
    // we might still want to POST if the backend expects it. For simplicity, let's rely on `id` and `!isNew`.
    const isUpdateOperation = photoToUpdate.id && !photoToUpdate.isNew;
    const url = isUpdateOperation
      ? `/api/admin/photos/${photoToUpdate.id}`
      : "/api/admin/photos";
    const method = isUpdateOperation ? "PUT" : "POST";

    const photoPayload = {
      image: editedDataUrl,
      // Include alt and caption, as they might have been edited in the main form before opening the editor
      image_alt_text: photoToUpdate.altText,
      caption: photoToUpdate.caption,
      has_rights_to_use: photoToUpdate.hasRightsToUse,
    };

    if (!isUpdateOperation) {
      // For new photos, add necessary metadata
      photoPayload.photographic_type = entityName;
      photoPayload.photographic_id = listingId; // This could be null if the listing itself is new

      if (collectionName === "hero_photos_attributes") {
        photoPayload.display_type = "hero_type";
      } else if (collectionName === "carousel_photos_attributes") {
        photoPayload.display_type = "carousel_type";
      }
      // Add featured status for new photos if applicable
      if (isCarousel) {
        photoPayload.featured = photoToUpdate.featured || false;
      }
    } else {
      // For existing photos being updated, we might also want to send 'featured' if it's a carousel
      if (isCarousel) {
        photoPayload.featured = photoToUpdate.featured;
      }
    }

    console.log("[ImageGalleryManager] AJAX Request Details:", {
      url,
      method,
      photoPayload: {
        ...photoPayload,
        image: photoPayload.image.substring(0, 30) + "...",
      },
    });

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ photo: photoPayload }),
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText };
        }
        console.error(
          "[ImageGalleryManager] Error saving photo:",
          response.status,
          errorData
        );
        // TODO: Provide user feedback
        throw new Error(
          errorData.message ||
            `Failed to save photo. Status: ${response.status}`
        );
      }

      const savedPhotoWrapper = await response.json(); // Expecting { photo: { ... } }
      const savedPhotoData = savedPhotoWrapper.photo;

      if (!savedPhotoData || !savedPhotoData.id) {
        console.error(
          "[ImageGalleryManager] Invalid photo data received from server:",
          savedPhotoWrapper
        );
        throw new Error("Invalid photo data received from server.");
      }

      console.log(
        "[ImageGalleryManager] Photo saved successfully on server:",
        savedPhotoData
      );

      setPhotos((prevPhotos) =>
        prevPhotos.map((p) => {
          if (p.clientId === photoToUpdate.clientId) {
            // This photo was the one edited. Update it with server data.
            return {
              ...p, // Keep other client-side state not returned by server if any
              id: savedPhotoData.id,
              imageUrlForFrontend: savedPhotoData.image_url_for_frontend,
              altText: savedPhotoData.image_alt_text || "", // Update from server
              caption: savedPhotoData.caption || "", // Update from server
              hasRightsToUse: savedPhotoData.has_rights_to_use || false,
              featured:
                savedPhotoData.featured !== undefined
                  ? savedPhotoData.featured
                  : p.featured, // Update from server
              isNew: false, // It's now persisted
              preview: null, // Clear client-side preview
              editedDataUrl: null, // Clear temp data URL
              file: null, // Clear original File object if it existed
              _destroy: false, // Ensure it's not marked for destruction
            };
          }
          return p;
        })
      );
      console.log(
        "[ImageGalleryManager] Client state updated for photo:",
        savedPhotoData.id
      );
    } catch (error) {
      console.error(
        "[ImageGalleryManager] AJAX request failed or error processing response:",
        error
      );
      // TODO: Provide user feedback, possibly revert optimistic UI changes if any were made
    } finally {
      handleCloseEditor();
    }
  };
  // --- End Editor Interaction Handlers ---

  const renderHiddenFields = () => {
    const fieldNamePrefix = `${entityName}[${collectionName}]`;
    let photoIndex = -1; // Cocoon-like indexing for persisted records

    return photos
      .map((photo) => {
        if (photo._destroy && photo.id) {
          // For persisted records marked for destruction
          photoIndex++;
          return (
            <React.Fragment key={photo.clientId || photo.id}>
              <input
                type="hidden"
                name={`${fieldNamePrefix}[${photoIndex}][id]`}
                value={photo.id}
              />
              <input
                type="hidden"
                name={`${fieldNamePrefix}[${photoIndex}][_destroy]`}
                value="1"
              />
            </React.Fragment>
          );
        }
        if (!photo._destroy) {
          // For new or existing non-destroyed photos
          photoIndex++;
          return (
            <React.Fragment key={photo.clientId}>
              {photo.id && !photo.isNew && (
                <input
                  type="hidden"
                  name={`${fieldNamePrefix}[${photoIndex}][id]`}
                  value={photo.id}
                />
              )}
              {/* For new photos, the 'image' field will be populated by Rails from the AJAX uploaded temp file */}
              {/* Or, if we send base64 directly, this needs adjustment server-side */}
              {/* For now, we assume AJAX uploads happen separately, and we're just managing metadata here for form submission */}
              <input
                type="hidden"
                name={`${fieldNamePrefix}[${photoIndex}][image_alt_text]`}
                value={photo.altText || ""}
              />
              <input
                type="hidden"
                name={`${fieldNamePrefix}[${photoIndex}][caption]`}
                value={photo.caption || ""}
              />
              <input
                type="hidden"
                name={`${fieldNamePrefix}[${photoIndex}][has_rights_to_use]`}
                value={photo.hasRightsToUse || false}
              />
              {isCarousel && (
                <input
                  type="hidden"
                  name={`${fieldNamePrefix}[${photoIndex}][featured]`}
                  value={photo.featured || false}
                />
              )}
              {/* _destroy for non-persisted records is handled by not rendering them if removed */}
            </React.Fragment>
          );
        }
        return null;
      })
      .concat(
        deletedPhotoIds.map((id, index) => {
          // This ensures _destroy for photos that were present initially and then removed,
          // but might be redundant if the above logic correctly handles _destroy for photos still in the 'photos' array.
          // The key is to find the correct index for cocoon.
          // This part needs careful review against how cocoon processes indices for destroyed items.
          // For now, let's assume the photos array itself correctly marks _destroy for existing.
          // This explicit loop for deletedPhotoIds might be simplified if photos array handles it.
          // Consider removing if covered by mapping `photos` array with `_destroy: true`.
          // For now, let's be cautious and keep a separate mechanism for already persisted photos.
          // The index here might clash. A safer way is to ensure `photos` contains all, including marked for destroy.
          // The current logic in handleRemovePhoto tries to do this.
          return null; // Simplified: rely on photos array state.
        })
      );
  };

  console.log(
    "[ImageGalleryManager] Rendering. isEditorOpen:",
    isEditorOpen,
    "editingPhoto:",
    editingPhoto
  );

  return (
    <div className="image-gallery-container">
      <div className="photo-grid">
        <div className="add-photo-card">
          <ImageUploader
            onUpload={handleFilesAdded}
            uploading={photos.some((p) => p.isLoading && p.isNew)}
          />
        </div>

        {photos.map((photo) => {
          if (photo._destroy) return null;

          const imgSrc =
            photo.preview || photo.imageUrlForFrontend || photo.image?.url;

          return (
            <div
              key={photo.clientId}
              className={`photo-card ${photo.isLoading ? "is-loading" : ""} ${
                photo.error ? "has-error" : ""
              }`}
            >
              <div className="photo-card__preview">
                {photo.isLoading && (
                  <div className="photo-card__loading-overlay">
                    <div className="spinner"></div>
                    <span>Uploading...</span>
                  </div>
                )}
                {photo.error && !photo.isLoading && (
                  <div className="photo-card__error-overlay">
                    <span>Error</span>
                  </div>
                )}

                {isCarousel &&
                  photo.featured &&
                  !photo.isLoading &&
                  !photo.error && (
                    <div className="photo-card__featured-badge">&#9733;</div>
                  )}

                {imgSrc ? (
                  <img src={imgSrc} alt={photo.altText || "Preview"} />
                ) : (
                  <div className="photo-card__no-preview-placeholder">
                    <span>No Preview</span>
                  </div>
                )}

                {!photo.isLoading && !photo.error && (
                  <div className="photo-card__actions-overlay">
                    <button
                      type="button"
                      className="action-btn edit-btn"
                      title="Edit Image"
                      onClick={() => handleOpenEditor(photo)}
                      disabled={photo.isLoading || !!photo.error}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="action-btn remove-btn"
                      title="Remove Image"
                      onClick={() => handleRemovePhoto(photo.clientId)}
                      disabled={photo.isLoading}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {!photo.isLoading && !photo.error && (
                <div className="photo-card__info">
                  <div>
                    <label htmlFor={`altText-${photo.clientId}`}>
                      Alt Text:
                    </label>
                    <textarea
                      id={`altText-${photo.clientId}`}
                      value={photo.altText}
                      onChange={(e) =>
                        handleInputChange(
                          photo.clientId,
                          "altText",
                          e.target.value
                        )
                      }
                      disabled={photo.isLoading}
                      rows={3}
                      placeholder="Describe the image for accessibility"
                    />
                    <button
                      type="button"
                      className="generate-alt-btn"
                      onClick={() => handleGenerateAlt(photo.clientId)}
                      disabled={
                        photo.isLoading ||
                        photo.isGeneratingAlt ||
                        !(
                          photo.imageUrlForFrontend ||
                          (photo.image && photo.image.url)
                        )
                      }
                      title={
                        photo.isGeneratingAlt
                          ? "Generating..."
                          : !(
                              photo.imageUrlForFrontend ||
                              (photo.image && photo.image.url)
                            )
                          ? "Upload must finish before generating"
                          : "Generate Alt with AI"
                      }
                    >
                      {photo.isGeneratingAlt
                        ? "Generating..."
                        : "Generate Alt with AI"}
                    </button>
                  </div>
                  {isCarousel && (
                    <div>
                      <label htmlFor={`caption-${photo.clientId}`}>
                        Caption:
                      </label>
                      <textarea
                        id={`caption-${photo.clientId}`}
                        value={photo.caption}
                        onChange={(e) =>
                          handleInputChange(
                            photo.clientId,
                            "caption",
                            e.target.value
                          )
                        }
                        disabled={photo.isLoading}
                        rows={2}
                        placeholder="Caption for the image (shown on website)"
                      />
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginTop: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`hasRightsToUse-${photo.clientId}`}
                      checked={!!photo.hasRightsToUse}
                      onChange={(e) =>
                        handleInputChange(
                          photo.clientId,
                          "hasRightsToUse",
                          e.target.checked
                        )
                      }
                      disabled={photo.isLoading}
                    />
                    <label
                      htmlFor={`hasRightsToUse-${photo.clientId}`}
                      className="has-rights-label"
                    >
                      I have the rights to use this image
                    </label>
                  </div>
                  {isCarousel && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginTop: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        id={`featured-${photo.clientId}`}
                        checked={!!photo.featured}
                        onChange={(e) =>
                          handleInputChange(
                            photo.clientId,
                            "featured",
                            e.target.checked
                          )
                        }
                        disabled={photo.isLoading}
                      />
                      <label
                        htmlFor={`featured-${photo.clientId}`}
                        className="featured-label"
                      >
                        Featured
                      </label>
                    </div>
                  )}
                </div>
              )}
              {photo.error && !photo.isLoading && (
                <div className="photo-card__error-message">
                  Error: {photo.error}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {photos.filter((p) => !p._destroy).length === 0 && (
        <div className="photo-grid--empty">
          <p>No images yet. Add an image to get started!</p>
        </div>
      )}

      <ListingPhotoEditor
        isOpen={isEditorOpen}
        photo={editingPhoto}
        onSave={handleSaveEditedImage}
        onClose={handleCloseEditor}
      />
      <div className="hidden-fields" style={{ display: "none" }}>
        {renderHiddenFields()}
      </div>
    </div>
  );
};

ImageGalleryManager.propTypes = {
  initialPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      image_url_for_frontend: PropTypes.string,
      image: PropTypes.shape({ url: PropTypes.string }), // From Carrierwave perhaps
      preview: PropTypes.string, // For client-side previews of new files
      image_alt_text: PropTypes.string,
      caption: PropTypes.string,
      featured: PropTypes.bool,
      has_rights_to_use: PropTypes.bool,
      _destroy: PropTypes.bool,
    })
  ),
  listingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityName: PropTypes.string.isRequired,
  collectionName: PropTypes.string.isRequired,
  isCarousel: PropTypes.bool,
  form: PropTypes.object, // Not directly used yet, but good to have if needed for complex_form
  csrfToken: PropTypes.string,
};

export default ImageGalleryManager;
